import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { Pagination, SearchBar, ButtonLink } from '@/shared/components';
import { useAddOnGroupsQuery } from '../hooks/useAddOnGroupsQuery';
import { useAddOnGroupMutations } from '../hooks/useAddOnGroupMutations';
import { AddOnGroupDataTable } from '../components/AddOnGroupDataTable';
import { ConfirmActionModal } from '@shared/components';

export function AddOnGroupList() {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [search, setSearch] = useState('');
    const [retireTargetId, setRetireTargetId] = useState<string | null>(null);

    const query = useAddOnGroupsQuery({ page, per_page: perPage, search: search || undefined });
    const groups = query.data?.data ?? [];
    const meta = query.data?.meta;

    const { retireMutation } = useAddOnGroupMutations();

    return (
        <section className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Optional Extra Groups</h1>
                <PermissionGuard permission={{ resource: 'smr', action: 'addon-group.add' }}>
                    <ButtonLink to="/smr-admin/addon-groups/new" size="sm">New Optional Extra Group</ButtonLink>
                </PermissionGuard>
            </div>

            <SearchBar searchTerm={search} setSearchTerm={setSearch} placeholder="Search optional extra groups..." />

            <div className="space-y-4">
                <AddOnGroupDataTable
                    data={groups}
                    isLoading={query.isLoading}
                    onRetire={(id) => setRetireTargetId(id)}
                />

                {meta && meta.last_page > 1 && (
                    <Pagination
                        currentPage={meta.current_page}
                        lastPage={meta.last_page}
                        total={meta.total}
                        perPage={meta.per_page}
                        from={meta.from}
                        to={meta.to}
                        onPageChange={setPage}
                        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                    />
                )}
            </div>

            <ConfirmActionModal
                isOpen={retireTargetId !== null}
                title="Retire optional extra group"
                message="Retire this group? Existing product assignments are preserved."
                confirmLabel="Retire"
                confirmVariant="warning"
                isPending={retireMutation.isPending}
                onConfirm={() => {
                    if (retireTargetId) retireMutation.mutate(retireTargetId);
                    setRetireTargetId(null);
                }}
                onClose={() => setRetireTargetId(null)}
            />
        </section>
    );
}
